// 🔧 public_entry.js
// ✅ Purpose: Public-facing entry submission form for events
// ✅ Features:
// - Requires sign-in or inline account creation before submitting an entry
// - Uses event slug context to load the active event
// - Collects first name, last name, email, entry title, optional description, Facebook, Instagram
// - Supports multiple image uploads with a max of 5 images
// - Limits each selected image to 1MB before upload
// - Shows upload progress for each image
// - Saves submissions to /events/{eventId}/pendingEntries
// - Marks entries as pending until a manager approves them
// - Uses the newer responsive Let's Go Vote layout pattern
// - Shows success/error notifications at the top of the page for 20 seconds

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EntryCandidateReview from "./entry_candidate_review";
import EntryAccountSignup from "../../components/entry_account_signup";
import {
  addDoc,
  collection,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { auth, db, storage } from "../../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { completeEmailRegistration } from "../../services/email_registration";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import { useCategories } from "../../hooks/use_categories";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const MAX_IMAGES = 5;
const MAX_FILE_SIZE_BYTES = 1024 * 1024;

const PublicEntrySubmission = () => {
  const { eventslug, eventId, eventDoc, eventLoading, ready } =
    useEventContext({
      redirectIfNotFound: true,
    });

  const { user } = useAuth();
  const { categories, loading: categoriesLoading } = useCategories(eventId);

  const fileInputRef = useRef(null);
  const pendingSubmissionRef = useRef(null);
  const registrationUserRef = useRef(null);
  const registrationCompleteRef = useRef(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    entryTitle: "",
    description: "",
    facebook: "",
    instagram: "",
    transactionId: "",
    requestedCategoryId: "",
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState(null);

  // ✅ Pre-fill known user info when available
  useEffect(() => {
    if (!user) return;

    setForm((prev) => ({
      ...prev,
      email: prev.email || user.email || "",
      firstName: prev.firstName || user.firstName || "",
      lastName: prev.lastName || user.lastName || "",
    }));
  }, [user]);

  // ✅ Auto-clear status after 20 seconds
  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length > MAX_IMAGES) {
      showStatus(setStatus, {
        type: "warning",
        message: `Please select no more than ${MAX_IMAGES} images.`,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setSelectedFiles([]);
      return;
    }

    if (files.some((file) => !file.type.startsWith("image/"))) {
      showStatus(setStatus, { type: "warning", message: "Please select image files only." });
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const oversizedFiles = files.filter(
      (file) => file.size > MAX_FILE_SIZE_BYTES
    );

    if (oversizedFiles.length > 0) {
      showStatus(setStatus, {
        type: "warning",
        message: "Each image must be 1MB or smaller.",
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(files);
    setUploadProgress({});
  };

  const uploadEntryImages = async (pendingEntryId) => {
    const uploadedImages = [];

    for (const [index, file] of selectedFiles.entries()) {
      const safeFileName = file.name
        .toLowerCase()
        .replace(/[^a-z0-9.-]/g, "-");

      const storagePath = `events/${eventId}/pending_entries/${pendingEntryId}/${index}-${safeFileName}`;

      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      const uploadedImage = await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const percent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );

            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: percent,
            }));
          },
          (error) => {
            reject(error);
          },
          async () => {
            try {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadUrl,
                storagePath,
                fileName: file.name,
                size: file.size,
                contentType: file.type,
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });

      uploadedImages.push(uploadedImage);
    }

    return uploadedImages;
  };

  const validateForm = () => {
    const missing = [];

    if (!form.firstName.trim()) missing.push("First Name");
    if (!form.lastName.trim()) missing.push("Last Name");
    if (!form.email.trim()) missing.push("Email");
    if (!form.entryTitle.trim()) missing.push("Entry Title");

    if (missing.length > 0) {
      showStatus(setStatus, {
        type: "warning",
        message: `Missing required fields: ${missing.join(", ")}`,
      });

      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      showStatus(setStatus, { type: "warning", message: "Please enter a valid email address." });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!user && !createAccount) {
      showStatus(setStatus, {
        type: "warning",
        message: "Please log in or create an account below before submitting an entry.",
      });

      return;
    }

    if (!eventId || submitting) return;
    if (eventDoc?.allowOnlineEntries !== true) {
      showStatus(setStatus, { type: "warning", message: "Online entries are closed for this event." });
      return;
    }

    if (!validateForm()) return;
    const registering = createAccount && (!user || registrationUserRef.current);
    if (registering && !registrationUserRef.current && (password.trim().length < 6 || !agreedToTerms)) {
      showStatus(setStatus, { type: "warning", message: "Use a password with at least 6 characters and accept the Terms & Conditions." });
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      let submittingUser = user;
      if (registering) {
        // Keep the created identity across profile/email/upload failures. Retrying
        // must finish the original account instead of attempting another signup.
        if (!registrationUserRef.current) {
          registrationUserRef.current = (await createUserWithEmailAndPassword(auth, form.email.trim(), password)).user;
          setPassword("");
        }
        submittingUser = registrationUserRef.current;
        if (!registrationCompleteRef.current) {
          await completeEmailRegistration(submittingUser, {
            firstName: form.firstName, lastName: form.lastName, agreedToTerms,
          });
          registrationCompleteRef.current = true;
          trackEvent({ action: GA_EVENTS.REGISTRATION_SUBMITTED, parameters: { method: "email" } });
        }
      }
      const pendingEntry = {
        eventId,
        eventSlug: eventslug || "",
        eventName: eventDoc?.name || "",
        uid: submittingUser.uid,
        userEmail: submittingUser.email || form.email,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        entryTitle: form.entryTitle.trim(),
        description: form.description.trim(),
        facebook: form.facebook.trim(),
        instagram: form.instagram.trim(),
        transactionId: form.transactionId.trim(),
        requestedCategoryId: form.requestedCategoryId,
        status: selectedFiles.length ? "uploading" : "pending",
        imageCount: selectedFiles.length,
        images: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Keep the same submission after upload failure so retry cannot create duplicates.
      if (!pendingSubmissionRef.current) {
        pendingSubmissionRef.current = await addDoc(
          collection(db, "events", eventId, "pendingEntries"), pendingEntry
        );
      }
      const pendingRef = pendingSubmissionRef.current;
      const uploadedImages = selectedFiles.length ? await uploadEntryImages(pendingRef.id) : [];
      if (selectedFiles.length) {
        await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(pendingRef);
          if (snapshot.data()?.status !== "uploading") throw new Error("Submission is no longer accepting images.");
          transaction.update(pendingRef, {
            pendingEntryId: pendingRef.id, images: uploadedImages,
            imageCount: uploadedImages.length, status: "pending", updatedAt: serverTimestamp(),
          });
        });
      }

      setSubmitted(true);

      showStatus(setStatus, {
        type: "success",
        message:
          "Your entry was submitted and is pending manager approval. Thank you!",
      });

      trackEvent({
          action: GA_EVENTS.PUBLIC_ENTRY_SUBMITTED,
          parameters: {
              event_slug: eventslug,
              event_type: eventDoc?.eventType,
              image_count: uploadedImages.length,
          },
      });

    } catch (error) {
      const signupErrors = {
        "auth/email-already-in-use": "This email is already in use. Please log in with your existing account.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/password-does-not-meet-requirements": "Choose a password that meets the account security requirements.",
        "auth/too-many-requests": "Too many attempts. Please try again later.",
      };
      if (!signupErrors[error.code]) console.error("Failed to submit public entry", { code: error.code || "unknown" });

      showStatus(setStatus, {
        type: "error",
        message: signupErrors[error.code] || (registrationUserRef.current && !registrationCompleteRef.current
          ? "Your account was created, but setup is incomplete. Please retry to finish setup and submit your entry."
          : "Something went wrong while submitting your entry."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const publicEntrySeo = {
    ...buildEventPageSeo({
      eventDoc,
      pageKey: "publicEntry",
      fallbackTitle: `Submit Entry | ${
        eventDoc?.name || "Event"
      } | Let's Go Vote`,
      fallbackDescription:
        "Submit an entry for manager approval.",
    }),

    path: `/${eventslug}/public_entry`,

    noindex: eventDoc?.allowOnlineEntries !== true || !isEventPublic(eventDoc),

    breadcrumbs: [
      { name: "Home", path: "/" },
      {
        name: eventDoc?.name || "Event",
        path: `/${eventslug}/event`,
      },
      {
        name: "Entries",
        path: `/${eventslug}/entries`,
      },
      {
        name: "Submit Entry",
        path: `/${eventslug}/public_entry`,
      },
    ],
  };

  if (!ready || eventLoading || categoriesLoading) {
    return <FullPageSpinner message="Loading entry form..." />;
  }

  if (eventDoc?.allowOnlineEntries !== true) {
    return <><SeoHead {...publicEntrySeo} /><section id="top-content" className="container py-4">
      <h1>Online Entries Closed</h1><p>This event is not accepting online entries.</p>
    </section></>;
  }

  return (
    <>
    <SeoHead {...publicEntrySeo} />
    <section id="top-content" className="container-fluid py-4">
      <div className="container" style={{ maxWidth: "800px" }}>
        <div className="text-center mb-4">
          <h1 className="fw-bold mb-2">Submit an Entry</h1>

          <p className="text-muted mb-0">
            Submit your entry for {eventDoc?.name || "this event"}. A manager
            will review it before it appears publicly.
          </p>
        </div>

        {status?.message && (
          <div
            className={`alert text-center ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {!user && (
          <div className="alert alert-warning text-center">
            Please log in or create an account below before submitting an entry.
          </div>
        )}

        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <h2 className="h5 fw-bold mb-0">Entry Information</h2>
          </div>

          <div className="card-body">
            {submitted ? (
              <div className="alert alert-success mb-0">
                ✅ Thank you! Your entry is pending manager approval.
                {registrationCompleteRef.current && <div className="mt-2">
                  Your account is ready. Check your email and verify your account before voting.
                  {" "}<a href="/dashboard?verifyRequired=1">Continue to your account</a>
                </div>}
              </div>
            ) : (
              <>
                <fieldset disabled={submitting || Boolean(pendingSubmissionRef.current) || Boolean(registrationUserRef.current)}><div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">
                      First Name <span className="text-danger">*</span>
                    </label>

                    <input
                      type="text"
                      name="firstName"
                      aria-label="First Name"
                      maxLength={100}
                      className="form-control"
                      value={form.firstName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">
                      Last Name <span className="text-danger">*</span>
                    </label>

                    <input
                      type="text"
                      name="lastName"
                      aria-label="Last Name"
                      maxLength={100}
                      className="form-control"
                      value={form.lastName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-bold">
                      Email <span className="text-danger">*</span>
                    </label>

                    <input
                      type="email"
                      name="email"
                      aria-label="Email"
                      maxLength={320}
                      className="form-control"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-bold">
                      Entry Title <span className="text-danger">*</span>
                    </label>

                    <input
                      type="text"
                      name="entryTitle"
                      aria-label="Entry Title"
                      maxLength={300}
                      className="form-control"
                      value={form.entryTitle}
                      onChange={handleChange}
                      placeholder="Example: 1967 Volkswagen Beetle, Handmade Quilt, Chili Verde"
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold" htmlFor="public-entry-category">Category</label>
                    <select id="public-entry-category" name="requestedCategoryId" className="form-select"
                      value={form.requestedCategoryId} onChange={handleChange}>
                      <option value="">No category (default)</option>
                      {categories.filter((category) => !category.isDefault && !category.placeholder && Number(category.id) !== 0).map((category) => (
                        <option key={category.firestoreId || category.id} value={String(category.firestoreId || category.id)}>{category.name}</option>
                      ))}
                    </select>
                    <div className="form-text">Choose the default or a named category. A manager will confirm your selection.</div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold" htmlFor="public-entry-transaction">Transaction ID</label>
                    <input id="public-entry-transaction" name="transactionId" type="text" className="form-control"
                      maxLength={200} value={form.transactionId} onChange={handleChange} aria-describedby="public-entry-transaction-help" />
                    <div id="public-entry-transaction-help" className="form-text">
                      Optional unique identifier from your event entry receipt. Managers use it to match this entry to its receipt; it does not verify payment. Do not enter card or bank details.
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">
                      Optional Description
                    </label>

                    <textarea
                      name="description"
                      aria-label="Optional Description"
                      maxLength={10000}
                      className="form-control"
                      rows="4"
                      value={form.description}
                      onChange={handleChange}
                      placeholder="Tell voters or event organizers a little more about your entry."
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Facebook</label>

                    <input
                      type="text"
                      name="facebook"
                      aria-label="Facebook"
                      maxLength={2048}
                      className="form-control"
                      value={form.facebook}
                      onChange={handleChange}
                      placeholder="Optional Facebook link or handle"
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Instagram</label>

                    <input
                      type="text"
                      name="instagram"
                      aria-label="Instagram"
                      maxLength={2048}
                      className="form-control"
                      value={form.instagram}
                      onChange={handleChange}
                      placeholder="Optional Instagram link or handle"
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-bold">
                      Images
                    </label>

                    <input
                      type="file"
                      aria-label="Images"
                      accept="image/*"
                      multiple
                      className="form-control"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />

                    <div className="form-text">
                      You may upload up to 5 images. Each image must be 1MB or
                      smaller.
                    </div>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="col-12">
                      <div className="border rounded p-3 bg-light">
                        <strong>Selected Images</strong>

                        {selectedFiles.map((file) => (
                          <div key={file.name} className="mt-3">
                            <div className="small mb-1">
                              {file.name} — {Math.round(file.size / 1024)} KB
                            </div>

                            <div className="progress">
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                  width: `${uploadProgress[file.name] || 0}%`,
                                }}
                              >
                                {uploadProgress[file.name] || 0}%
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                </fieldset>
                <EntryAccountSignup user={user} accountCreated={Boolean(registrationUserRef.current)}
                  createAccount={createAccount} onCreateAccountChange={(checked) => { setCreateAccount(checked); setPassword(""); }}
                  password={password} onPasswordChange={setPassword} agreedToTerms={agreedToTerms}
                  onTermsChange={setAgreedToTerms} disabled={submitting} />
                <div className="d-grid mt-4">
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting || (!user && !createAccount)}
                  >
                    {submitting
                      ? "Submitting..."
                      : createAccount && (!user || registrationUserRef.current) && !registrationCompleteRef.current ? "Create Account and Submit Entry" : "✅ Submit Entry for Approval"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

// Invitations review an existing manager entry, independently of public intake.
const PublicEntry = () => {
  const [searchParams] = useSearchParams();
  const entryId = searchParams.get("entry");
  return searchParams.has("entry") ? <EntryCandidateReview key={entryId || "invalid"} entryId={entryId || ""} /> : <PublicEntrySubmission />;
};

export default PublicEntry;
